// PWA install prompt.
//
// Chromium fires `beforeinstallprompt` once, early, and only to listeners that
// already exist, so captureInstallPrompt() runs from main.tsx before React
// mounts and holds the event until someone taps Install. Safari never fires it:
// on iPhone and iPad the only route is Share → Add to Home Screen, and that is
// also the only way an iOS visitor can receive Web Push at all.
import { useSyncExternalStore } from 'react'
import { isNativeShell } from './shell'

type InstallChoice = { outcome: 'accepted' | 'dismissed' }
interface BeforeInstallPromptEvent extends Event { prompt: () => Promise<void>; userChoice: Promise<InstallChoice> }

/**
 * installed: running from the Home Screen. available: one tap installs.
 * ios: Safari's Share → Add to Home Screen. ios-browser: Chrome/Edge/Firefox on
 * iPhone, same steps from their own Share button. android: no one-tap prompt
 * yet, so the browser menu's Install / Add to Home screen. in-app: inside
 * WhatsApp, Instagram or Facebook, which cannot install — open the browser
 * first. unavailable: nothing to offer (desktop, native shell).
 */
export type InstallState = 'installed' | 'available' | 'ios' | 'ios-browser' | 'android' | 'in-app' | 'unavailable'

let deferred: BeforeInstallPromptEvent | null = null
let installed = false
let captured = false
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((listener) => listener())

export const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false
  const nav = navigator as Navigator & { standalone?: boolean }
  return nav.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches === true
}

const isIos = (): boolean =>
  // iPadOS reports a desktop Mac user agent; touch points tell it apart.
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

export const isIosSafari = (): boolean => {
  if (typeof navigator === 'undefined') return false
  return isIos() && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent)
}

// Shared links open inside the chat app's own browser, which has no install
// option at all. Most visitors arrive this way, from a WhatsApp group.
const IN_APP_BROWSER = /FBAN|FBAV|FB_IAB|Instagram|WhatsApp|Snapchat|TikTok|musical_ly|Line\/|; wv\)/

const manualState = (): InstallState => {
  if (typeof navigator === 'undefined') return 'unavailable'
  if (IN_APP_BROWSER.test(navigator.userAgent)) return 'in-app'
  if (isIos()) return isIosSafari() ? 'ios' : 'ios-browser'
  if (/Android/.test(navigator.userAgent)) return 'android'
  return 'unavailable'
}

export const installState = (): InstallState => {
  if (typeof window === 'undefined' || isNativeShell()) return 'unavailable'
  if (installed || isStandalone()) return 'installed'
  if (deferred) return 'available'
  return manualState()
}

export const captureInstallPrompt = (): void => {
  if (captured || typeof window === 'undefined' || isNativeShell()) return
  captured = true
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferred = event as BeforeInstallPromptEvent
    emit()
  })
  window.addEventListener('appinstalled', () => { installed = true; deferred = null; emit() })
}

/** Shows the browser's install dialog. Resolves true when the visitor accepts. */
export const promptInstall = async (): Promise<boolean> => {
  const event = deferred
  if (!event) return false
  // The event is single-use; the browser fires a fresh one if the visitor declines.
  deferred = null
  emit()
  await event.prompt()
  const { outcome } = await event.userChoice
  if (outcome === 'accepted') { installed = true; emit() }
  return outcome === 'accepted'
}

const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener) } }
export const useInstallState = (): InstallState => useSyncExternalStore(subscribe, installState, () => 'unavailable')

/** Test seam: forget captured state between cases. */
export const resetInstallPromptForTests = () => { deferred = null; installed = false; captured = false; listeners.clear() }
